import { createContext, type ComponentPropsWithRef, use } from "react";
import {
  Root as SelectRoot,
  Value as SelectValuePrimitive,
  Trigger as SelectTriggerPrimitive,
  Icon as SelectIconPrimitive,
  Portal as SelectPortalPrimitive,
  Content as SelectContentPrimitive,
  ScrollUpButton as SelectScrollUpButtonPrimitive,
  ScrollDownButton as SelectScrollDownButtonPrimitive,
  Viewport as SelectViewportPrimitive,
  Label as SelectLabelPrimitive,
  Item as SelectItemPrimitive,
  ItemIndicator as SelectItemIndicatorPrimitive,
  ItemText as SelectItemTextPrimitive,
  Separator as SelectSeparatorPrimitive,
} from "@radix-ui/react-select";
import { Check, ChevronDown, ChevronUp } from "lucide-react";

import { Paper } from "@/components/atoms/paper";
import {
  DROPDOWN_PANEL_SELECT_ITEM_CLASS,
  DROPDOWN_PANEL_SHELL_CLASS,
} from "@/components/molecules/dropdownPanel/dropdownPanel";
import { cn } from "@/lib/utils";
import {
  RADIX_CONTENT_ANIMATION_CLASSES,
  RADIX_CONTENT_POPPER_TRANSLATE,
} from "@/lib/ui/radixContentClasses";

const SelectDropdownPanelContext = createContext(false);

const Select = SelectRoot;

const SelectValue = SelectValuePrimitive;

const SelectTrigger = ({
  className,
  children,
  ref,
  ...props
}: ComponentPropsWithRef<typeof SelectTriggerPrimitive>) => (
  <SelectTriggerPrimitive
    ref={ref}
    className={cn(
      `
        flex h-9 w-full cursor-pointer items-center justify-between rounded-sm
        border border-input bg-transparent px-3 py-2 text-sm whitespace-nowrap
        shadow-sm ring-offset-background
        focus:ring-1 focus:ring-ring focus:outline-none
        disabled:cursor-not-allowed disabled:opacity-50
        data-placeholder:text-foreground/70
        [&>span]:line-clamp-1
      `,
      className,
    )}
    {...props}
  >
    {children}
    <SelectIconPrimitive asChild>
      <ChevronDown className="size-4 opacity-50" />
    </SelectIconPrimitive>
  </SelectTriggerPrimitive>
);
SelectTrigger.displayName = SelectTriggerPrimitive.displayName;

const SelectScrollUpButton = ({
  className,
  ref,
  ...props
}: ComponentPropsWithRef<typeof SelectScrollUpButtonPrimitive>) => (
  <SelectScrollUpButtonPrimitive
    ref={ref}
    className={cn(
      "flex cursor-pointer items-center justify-center py-1",
      className,
    )}
    {...props}
  >
    <ChevronUp className="size-4" />
  </SelectScrollUpButtonPrimitive>
);
SelectScrollUpButton.displayName = SelectScrollUpButtonPrimitive.displayName;

const SelectScrollDownButton = ({
  className,
  ref,
  ...props
}: ComponentPropsWithRef<typeof SelectScrollDownButtonPrimitive>) => (
  <SelectScrollDownButtonPrimitive
    ref={ref}
    className={cn(
      "flex cursor-pointer items-center justify-center py-1",
      className,
    )}
    {...props}
  >
    <ChevronDown className="size-4" />
  </SelectScrollDownButtonPrimitive>
);
SelectScrollDownButton.displayName =
  SelectScrollDownButtonPrimitive.displayName;

const SelectContent = ({
  className,
  children,
  position = "popper",
  dropdownPanel = false,
  ref,
  ...props
}: ComponentPropsWithRef<typeof SelectContentPrimitive> & {
  dropdownPanel?: boolean;
}) => (
  <SelectPortalPrimitive>
    <SelectContentPrimitive ref={ref} position={position} asChild {...props}>
      <Paper
        data-dropdown-panel={dropdownPanel ? "" : undefined}
        className={cn(
          dropdownPanel
            ? cn(
                `
                  relative z-50 max-h-[--radix-select-content-available-height]
                  min-w-32 overflow-hidden text-foreground
                `,
                DROPDOWN_PANEL_SHELL_CLASS,
              )
            : `
              relative z-50 max-h-[--radix-select-content-available-height]
              min-w-32 overflow-hidden bg-popover text-popover-foreground
            `,
          "origin-[--radix-select-content-transform-origin]",
          RADIX_CONTENT_ANIMATION_CLASSES,
          position === "popper" && RADIX_CONTENT_POPPER_TRANSLATE,
          className,
        )}
      >
        <SelectDropdownPanelContext value={dropdownPanel}>
          <SelectScrollUpButton />
          <SelectViewportPrimitive
            className={cn(
              "max-h-[--radix-select-content-available-height] overflow-y-auto",
              dropdownPanel ? "p-0" : "p-1",
              position === "popper" &&
                `
                  h-radix-select-trigger w-full
                  min-w-(--radix-select-trigger-width)
                `,
            )}
          >
            {children}
          </SelectViewportPrimitive>
          <SelectScrollDownButton />
        </SelectDropdownPanelContext>
      </Paper>
    </SelectContentPrimitive>
  </SelectPortalPrimitive>
);
SelectContent.displayName = SelectContentPrimitive.displayName;

const SelectLabel = ({
  className,
  ref,
  ...props
}: ComponentPropsWithRef<typeof SelectLabelPrimitive>) => (
  <SelectLabelPrimitive
    ref={ref}
    className={cn("px-2 py-1.5 text-sm font-semibold", className)}
    {...props}
  />
);
SelectLabel.displayName = SelectLabelPrimitive.displayName;

const SelectItem = ({
  className,
  children,
  ref,
  ...props
}: ComponentPropsWithRef<typeof SelectItemPrimitive>) => {
  const dropdownPanel = use(SelectDropdownPanelContext);

  return (
    <SelectItemPrimitive
      ref={ref}
      data-slot="select-item"
      className={cn(
        dropdownPanel
          ? DROPDOWN_PANEL_SELECT_ITEM_CLASS
          : `
            relative flex w-full cursor-pointer items-center rounded-sm py-1.5
            pr-8 pl-2 text-sm outline-none select-none
            focus:bg-accent focus:text-accent-foreground
            data-disabled:pointer-events-none data-disabled:opacity-50
          `,
        className,
      )}
      {...props}
    >
      <span
        data-slot="select-item-indicator"
        className={cn(
          "absolute right-2 flex size-3.5 items-center justify-center",
          dropdownPanel && "hidden",
        )}
      >
        <SelectItemIndicatorPrimitive>
          <Check className="size-4" />
        </SelectItemIndicatorPrimitive>
      </span>
      <SelectItemTextPrimitive>{children}</SelectItemTextPrimitive>
    </SelectItemPrimitive>
  );
};
SelectItem.displayName = SelectItemPrimitive.displayName;

const SelectSeparator = ({
  className,
  ref,
  ...props
}: ComponentPropsWithRef<typeof SelectSeparatorPrimitive>) => (
  <SelectSeparatorPrimitive
    ref={ref}
    className={cn("-mx-1 my-1 h-px bg-black/5", className)}
    {...props}
  />
);
SelectSeparator.displayName = SelectSeparatorPrimitive.displayName;

export { Select, SelectValue, SelectTrigger, SelectContent, SelectItem };
